import React, { useState, useEffect } from 'react';
import { ButtonTextData } from '@Types/common';
import { AnalyticsParamBuilder, textToBytes, validateUrl } from '@Utils/common';
import { useGenerateShortURLMutation } from '../../apiSlice';
import posthog from 'posthog-js';
import { displayToast } from '@Utils/toast';



const TextButton: React.FC<{
  index: number;
  automationId: any;
  data?: { text: string; buttonText: ButtonTextData[], type: 'text_message' };
  update_response_data: (data: { text: string; buttonText: ButtonTextData[], type: 'text_message' }) => void;
}> = ({
  data,
  automationId,
  update_response_data,
}) => {
  const [text, setText] = useState(data?.text || '');
  const [buttonText, setButtonText] = useState<ButtonTextData[]>(data?.buttonText || []);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [buttonErrors, setButtonErrors] = useState<{ [key: number]: { title?: string; url?: string } }>({});

  const [generateShortURL, { isLoading: generateShortURLIsLoading }] = useGenerateShortURLMutation();

  // Update parent component when data changes
  useEffect(() => {
    update_response_data({ text, buttonText, type: 'text_message'});
  }, [text, buttonText]);

  const handleButtonTextChange = (newText: string) => {
    setText(newText);
  };

  const handleButtonDataChange = (newButtons: ButtonTextData[]) => {
    const errors: { [key: number]: { title?: string; url?: string } } = {};
    newButtons.forEach((btn, idx) => {
      const e: { title?: string; url?: string } = {};
      if (!btn.title || btn.title.trim().length === 0) {
        e.title = 'Button text is required';
      } else if (btn.title.length > 20) {
        e.title = 'Button text must be ≤ 20 characters';
      }
      if (btn.type === 'link') {
        if (!btn.data || btn.data.trim().length === 0) {
          e.url = 'URL is required';
        } else if (!validateUrl(btn.data)) {
          e.url = 'Please enter a valid URL, must start with "https://"';
        }
      }
      if (e.title || e.url) {
        errors[idx] = e;
      }
    });
    setButtonErrors(errors);
    setButtonText(newButtons);
  };

  const handleSaveButton = async (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: "ig_text_button",
      classValue: "button_text_save",
      // genus: `${automationId}_${idx}`,
    }));

    // Validate fields before save
    const current = buttonText[idx];
    const errors: { title?: string; url?: string } = {};
    if (!current.title || current.title.trim().length === 0) {
      errors.title = 'Button text is required';
    } else if (current.title.length > 20) {
      errors.title = 'Button text must be ≤ 20 characters';
    }
    if (current.type === 'link') {
      if (!current.data || current.data.trim().length === 0) {
        errors.url = 'URL is required';
      } else if (!validateUrl(current.data)) {
        errors.url = 'Please enter a valid URL, must start with "https://"';
      }
    }
    if (errors.title || errors.url) {
      setButtonErrors(prev => ({ ...prev, [idx]: errors }));
      return;
    }

    const resp = await generateShortURL({
      original_url: buttonText[idx].data,
      automation_id: automationId
    }).unwrap();

    if (resp?.short_url) {
      const newButtons = [...buttonText];
      newButtons[idx] = { ...buttonText[idx], short_url: resp?.short_url };
      handleButtonDataChange(newButtons);
    }

    setIsEditing(null);
  };

  const OnTextBlurAnalytics = (input_component: string, text: string, type: string) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: input_component,
      classValue: "text_blur",
      str_value: text,
      genus: type
    }));
  };

  const OnIGTextButtonCancelClick = (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: "ig_text_button",
      classValue: "button_text_cancel",
      // genus: `${automationId}_${idx}`,
    }));
    const newButtons = buttonText.filter((_, i) => i !== idx);
    handleButtonDataChange(newButtons);
    setIsEditing(null);
  };

  const OnEditButtonClick = (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: "ig_text_button",
      classValue: "button_text_edit_icon",
      // genus: `${automationId}_${idx}`,
    }));
    setIsEditing(idx);
  };

  const OnDeleteButtonClick = (idx: number) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: "ig_text_button",
      classValue: "button_text_delete",
      // genus: `${automationId}_${idx}`,
    }));
    const newButtons = buttonText.filter((_, i) => i !== idx);
    handleButtonDataChange(newButtons);
    setIsEditing(null);
  };

  const OnAddButtonClick = () => {
    if (isEditing != null) {
      displayToast("error" ,'Please save the current button first.');
      return
    }
    posthog.capture("ui_action", AnalyticsParamBuilder({
      phylum: "ig_text_button",
      classValue: "button_text_add",
      genus: buttonText.length,
    }));
    const newButtons = [...buttonText, { title: '', data: '', type: 'link' as const }];
    handleButtonDataChange(newButtons);
    setIsEditing(newButtons.length - 1);
  };

  return (
    <div className="space-y-4 bg-white border border-grey-300 p-3 rounded-lg my-3">
      {/* <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#7373ff] rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
            </svg>
          </div>
          <h4 className="font-medium">Button Message</h4>
        </div>
      </div> */}

      <div className="">
        <div className="space-y-4">
          <div className="relative w-full">
            <textarea
              value={text}
              onChange={(e) => handleButtonTextChange(e.target.value)}
              placeholder="Enter text..."
              className="h-min-[150px] w-full rounded-xl p-3 border-2 border-dotted"
              onBlur={() => OnTextBlurAnalytics('ig_text', text, 'text_area')}
              style={{
                height: `${40 + text.split("\n").length * 20}px`,
                outline: 'none'
              }}
            />
            <span className={`absolute right-4 bottom-[-20px] text-xs ` + (textToBytes(text).length > 1000 ? "text-red-400" : "text-gray-400")}>
              {text.length}/1000
            </span>
          </div>
          
          <div className="flex flex-col gap-4">
            {buttonText.map((btn, idx) => (
              <div key={idx} className="space-y-2">
                {isEditing === idx ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2">
                      <div className="relative w-full">
                        <input
                          value={btn.title}
                          onChange={(e) => {
                            const newValue = e.target.value.slice(0, 20);
                            const newButtons = [...buttonText];
                            newButtons[idx] = { ...btn, title: newValue };
                            handleButtonDataChange(newButtons);
                          }}
                          onBlur={() => OnTextBlurAnalytics('ig_text_button_text', btn.title, `button_${idx}`)}
                          placeholder="Enter Button Text"
                          className={`py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none ${buttonErrors[idx]?.title ? 'border-red-400' : 'focus:border-purple-400'}`}
                          maxLength={20}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                          {btn.title.length}/20
                        </span>
                      </div>
                      <select
                        value={btn.type}
                        onChange={(e) => {
                          const newButtons = [...buttonText];
                          newButtons[idx] = { ...btn, type: e.target.value as 'link' | 'postback' };
                          handleButtonDataChange(newButtons);
                        }}
                        className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                      >
                        <option value="link">Link</option>
                        <option value="message" disabled>Postback</option>
                      </select>
                    </div>
                    {buttonErrors[idx]?.title && (
                      <p className="text-sm text-red-500">{buttonErrors[idx]?.title}</p>
                    )}
                    {btn.type === 'link' && (
                      <input
                        value={btn.data}
                        onChange={(e) => {
                          const newButtons = [...buttonText];
                          newButtons[idx] = { ...btn, data: e.target.value };
                          handleButtonDataChange(newButtons);
                        }}
                        onBlur={() => OnTextBlurAnalytics('ig_text_button_link', btn.data, `button_${idx}`)}
                        placeholder="Enter URL"
                        className={`w-full py-2 px-4 rounded-xl border-2 border-dotted focus:outline-none ${buttonErrors[idx]?.url ? 'border-red-400' : 'focus:border-purple-400'}`}
                      />
                    )}
                    {buttonErrors[idx]?.url && (
                      <p className="text-sm text-red-500">{buttonErrors[idx]?.url}</p>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => OnIGTextButtonCancelClick(idx)}
                        disabled={generateShortURLIsLoading}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveButton(idx)}
                        disabled={generateShortURLIsLoading || !!buttonErrors[idx]?.title || !!buttonErrors[idx]?.url}
                        className="px-4 py-2 bg-[#7373ff] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {generateShortURLIsLoading && <span className='loading_spinner'></span>}Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={"flex items-center justify-between gap-2 py-2 px-4 rounded-xl border group relative" + (btn.title === "" || btn.data === "" ? ' border-red-400 border-2 bg-red-200' : '')}>
                    <div className="flex text-center gap-2 w-full">
                      <span className="text-sm font-medium w-full">{btn.title}</span>
                    </div>
                    <div className="flex items-center gap-3 transition-opacity">
                      <button
                        onClick={() => OnEditButtonClick(idx)}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => OnDeleteButtonClick(idx)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {buttonText.length < 3 && !isEditing && (
            <button
              onClick={OnAddButtonClick}
              className="w-full py-2 px-4 border-2 border-dashed rounded-xl hover:border-purple-400 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm text-gray-500">Add Button</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextButton;